import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { TemplateType } from 'components/FolderTemplatesDialog/Templates'
import { WorkflowTemplate } from 'components/FolderTemplatesDialog/Types'
import { useFolder } from 'hooks/queries/folders/useFolder'
import useIsMounted from 'ismounted'
import { useEffect, useState } from 'react'
import { IWorkflow } from 'stores/Workflows/Types'
import { reportError } from 'utils/error'
import { v4 as uuid } from 'uuid'

interface Props {
  folderId: string
}

export const useInheritableTemplate = ({ folderId }: Props) => {
  const [template, setTemplate] = useState<WorkflowTemplate>()
  const folderData = useFolder(folderId).data
  const parentFolderId = folderData?.folder.parent_id
  const isMounted = useIsMounted()

  useEffect(() => {
    if (!parentFolderId) return

    sendToApi<{ workflow: IWorkflow }>(`/folders/${parentFolderId}/workflow`).then((response) => {
      if (response.type === SUCCESS_RESPONSE_TYPE && isMounted.current) {
        const workflow = response.data.workflow

        try {
          const templatizedStatuses = templatizeWorkflowStatuses(workflow)
          setTemplate({
            id: TemplateType.Custom,
            title: () => 'Parent folder',
            emoji: '🍭',
            statuses: templatizedStatuses,
          })
        } catch (e) {
          reportError(e)
        }
      }
    })
  }, [parentFolderId, isMounted])

  return template
}

const templatizeWorkflowStatuses = (workflow: IWorkflow): WorkflowTemplate['statuses'] => {
  const mappedIds = new Map<number, string>()

  workflow.property.property_values.forEach((value) => mappedIds.set(value.id, uuid()))

  const configuration: WorkflowTemplate['statuses']['configuration'] = {}
  Object.entries(workflow.configuration).forEach(([id, config]) => {
    const mappedId = mappedIds.get(parseInt(id))
    if (!mappedId) {
      throw new Error('Could not find mapped id')
    }
    configuration[mappedId] = config
  })

  const propertyValues: WorkflowTemplate['statuses']['property_values'] = workflow.property.property_values.map(
    (value) => {
      const { id, property_id, connected_to_id, ...rest } = value

      const uuid = mappedIds.get(id)
      if (!uuid) {
        throw new Error('Could not find uuid')
      }

      let connectedToUuid = null
      if (value.connected_to_id) {
        connectedToUuid = mappedIds.get(value.connected_to_id)
        if (!connectedToUuid) {
          throw new Error('Could not find mapped connected id')
        }
      }

      return {
        ...rest,
        uuid,
        connected_to_id: null,
        connected_to_uuid: connectedToUuid,
      }
    },
  )

  return {
    configuration,
    property_values: propertyValues,
  }
}
